/**
 * StreamingMessage component for displaying typing indicators and streaming text.
 *
 * Features:
 * - Animated typing indicator (bouncing dots) when no content yet
 * - Displays streaming text with pulse animation
 * - Blinking cursor at the end of streaming content
 */

import { useDirection } from '../i18n/useDirection';

export interface StreamingMessageProps {
  /** Current streaming content (empty string if just started) */
  content: string;
  /** Whether we're actively receiving chunks */
  isStreaming: boolean;
  /** Background color for the message bubble */
  backgroundColor?: string;
}

/**
 * Animated typing indicator with three bouncing dots.
 */
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 py-1">
      <span
        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
        style={{ animationDelay: '0ms', animationDuration: '600ms' }}
      />
      <span
        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
        style={{ animationDelay: '150ms', animationDuration: '600ms' }}
      />
      <span
        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
        style={{ animationDelay: '300ms', animationDuration: '600ms' }}
      />
    </div>
  );
}

/**
 * Streaming message bubble component.
 *
 * Shows either:
 * - A typing indicator (bouncing dots) when content is empty
 * - The streaming text with a blinking cursor when content is arriving
 */
export function StreamingMessage({
  content,
  isStreaming,
  backgroundColor = '#f3f4f6',
}: StreamingMessageProps) {
  const { isRTL } = useDirection();
  const showTypingIndicator = isStreaming && content.length === 0;
  const showContent = content.length > 0;

  return (
    <div className="flex justify-start">
      <div
        className={`
          max-w-[80%] px-4 py-2 rounded-2xl
          text-gray-900
          ${isStreaming ? 'animate-pulse' : ''}
        `}
        // The bubble's tail sits on the side the bot speaks from, which swaps in a mirrored chat.
        style={{ backgroundColor, [isRTL ? 'borderBottomRightRadius' : 'borderBottomLeftRadius']: '4px' }}
      >
        {showTypingIndicator && <TypingIndicator />}
        {showContent && (
          <p className="text-sm whitespace-pre-wrap inline" dir="auto">
            {content}
            {isStreaming && (
              <span
                className="inline-block w-0.5 h-4 bg-gray-600 animate-blink align-middle"
                style={{ marginInlineStart: '2px' }}
              />
            )}
          </p>
        )}
      </div>
    </div>
  );
}

export default StreamingMessage;
